import type { Dir, TrailEvent, TrailSpec, Val, ScoreBreakdown } from "../types";
import { DIR_DELTA, DIRS, WALK_MS } from "./config";
import { pointsForCorrect, finalizeScore } from "./scoring";
import { renderTokensHtml, valEq } from "../math/engine";
import * as audio from "../audio";
import type { Settings } from "../types";
import type { StampId } from "../cosmetics";

export interface BoardLook {
  scoutSrc: string;
  glow: string;
  stamp: StampId;
}

export interface BoardHooks {
  onHud: (h: { score: number; streak: number; combo: string; remainingMs: number }) => void;
  onComplete: (b: ScoreBreakdown, events: TrailEvent[]) => void;
  onTimeout: (b: ScoreBreakdown, events: TrailEvent[]) => void;
  onShake?: (dir: Dir) => void;
}

type Phase = "idle" | "walk" | "wrong" | "done" | "timeout" | "paused";

export class Board {
  spec: TrailSpec;
  settings: Settings;
  hooks: BoardHooks;
  canvas: HTMLCanvasElement;
  overlay: HTMLElement;
  ctx: CanvasRenderingContext2D;
  art: HTMLImageElement | null = null;
  pipImg: HTMLImageElement | null = null;
  look: BoardLook;
  visited: boolean[][];
  stepI = 0;
  triesOnTile = 0;
  score = 0;
  streak = 0;
  streakBest = 0;
  misses = 0;
  firstTry = 0;
  remainingMs: number;
  lastTs = 0;
  phase: Phase = "idle";
  walkT = 0;
  walkFrom = { x: 0, y: 0 };
  walkTo = { x: 0, y: 0 };
  pip = { r: 0, c: 0 };
  facing: Dir = "E";
  events: TrailEvent[] = [];
  startAt = 0;
  raf = 0;
  reduced: boolean;
  revealT = 0;
  wrongDir: Dir | null = null;
  wrongT = 0;
  hopT = 0;
  destroyed = false;
  cell = 0;
  pad = 8;
  swipe: { x: number; y: number } | null = null;

  constructor(canvas: HTMLCanvasElement, overlay: HTMLElement, spec: TrailSpec, settings: Settings, hooks: BoardHooks, look?: BoardLook) {
    this.canvas = canvas;
    this.overlay = overlay;
    this.spec = spec;
    this.settings = settings;
    this.hooks = hooks;
    this.look = look || { scoutSrc: "assets/art/pip.png", glow: "rgba(255,244,220,0.45)", stamp: "paw" };
    this.ctx = canvas.getContext("2d")!;
    this.reduced = settings.reducedMotion || matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.remainingMs = spec.timeLimitMs;
    this.visited = Array.from({ length: spec.size }, () => Array(spec.size).fill(false));
    const s = spec.path[0];
    this.pip = { r: s.r, c: s.c };
    this.visited[s.r][s.c] = true;
    this.startAt = performance.now();
    this.loadArt();
    this.bind();
    this.layout();
    requestAnimationFrame(() => this.layout());
    this.drawStones();
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
    this.emitHud();
  }

  loadArt() {
    const img = new Image();
    img.onload = () => {
      this.art = img;
    };
    img.src = `assets/illustrations/${this.spec.art}`;
    const pip = new Image();
    pip.onload = () => {
      this.pipImg = pip;
    };
    pip.src = this.look.scoutSrc;
  }

  ro: ResizeObserver | null = null;
  hudAcc = 0;

  bind() {
    this.onKey = this.onKey.bind(this);
    window.addEventListener("keydown", this.onKey);
    this.canvas.addEventListener("pointerdown", (e) => {
      this.swipe = { x: e.clientX, y: e.clientY };
    });
    this.canvas.addEventListener("pointerup", (e) => {
      if (!this.swipe) return;
      const dx = e.clientX - this.swipe.x;
      const dy = e.clientY - this.swipe.y;
      this.swipe = null;
      if (Math.hypot(dx, dy) < 28) return;
      const dir: Dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "E" : "W") : dy > 0 ? "S" : "N";
      this.tryDir(dir);
    });
    window.addEventListener("resize", this.layout);
    const wrap = this.canvas.parentElement;
    if (wrap && typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.layout());
      this.ro.observe(wrap);
    }
  }

  onKey(e: KeyboardEvent) {
    if (this.phase === "paused" || this.phase === "done") return;
    const map: Record<string, Dir> = {
      ArrowUp: "N",
      ArrowDown: "S",
      ArrowLeft: "W",
      ArrowRight: "E",
      w: "N",
      W: "N",
      a: "W",
      A: "W",
      s: "S",
      S: "S",
      d: "E",
      D: "E",
    };
    const d = map[e.key];
    if (d) {
      e.preventDefault();
      this.tryDir(d);
    }
    if (e.key === " " && this.settings.readAloud) {
      e.preventDefault();
      audio.speak(this.spec.steps[this.stepI]?.problem.speak || "");
    }
  }

  layout = () => {
    const wrap = this.canvas.parentElement!;
    const w = wrap.clientWidth || 360;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(w * dpr);
    this.canvas.style.width = w + "px";
    this.canvas.style.height = w + "px";
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.pad = Math.max(6, w * 0.02);
    this.cell = (w - this.pad * 2) / this.spec.size;
    this.drawStones();
  };

  cellCenter(r: number, c: number) {
    return {
      x: this.pad + (c + 0.5) * this.cell,
      y: this.pad + (r + 0.5) * this.cell,
    };
  }

  tryDir(dir: Dir) {
    if (this.phase !== "idle") return;
    const step = this.spec.steps[this.stepI];
    if (!step) return;
    this.triesOnTile += 1;
    const chosen = step.stones[dir];
    const ok = dir === step.correctDir && valEq(chosen, step.problem.answer);
    const ev: TrailEvent = {
      step: this.stepI,
      tile: [step.cell.r, step.cell.c],
      expr: step.problem.tokens.map((t) => (t as any).v ?? "").join(" "),
      correctCompact: step.problem.answer.compact,
      chosenCompact: chosen.compact,
      dir,
      ok,
      tryIndex: this.triesOnTile,
      t: Math.round(performance.now() - this.startAt),
    };
    this.events.push(ev);

    if (ok) {
      this.streak += 1;
      if (this.streak > this.streakBest) this.streakBest = this.streak;
      this.score += pointsForCorrect(this.triesOnTile, this.streak);
      if (this.triesOnTile === 1) this.firstTry += 1;
      audio.moveSfx();
      audio.correctChime();
      const next = this.spec.path[this.stepI + 1];
      this.visited[next.r][next.c] = true;
      this.facing = dir;
      this.walkFrom = this.cellCenter(this.pip.r, this.pip.c);
      this.walkTo = this.cellCenter(next.r, next.c);
      this.pip = { r: next.r, c: next.c };
      this.stepI += 1;
      this.triesOnTile = 0;
      this.walkT = 0;
      this.phase = this.reduced ? "idle" : "walk";
      const stonesEl = this.overlay.querySelector(".stones");
      if (stonesEl) stonesEl.classList.add("hidden");
      if (this.reduced) this.afterWalk();
      this.emitHud(this.streak >= 3 ? `Streak × ${pointsMultLabel(this.streak)} — keep going!` : "");
    } else {
      this.streak = 0;
      this.misses += 1;
      this.remainingMs = Math.max(0, this.remainingMs - 3000);
      this.wrongDir = dir;
      this.wrongT = 0;
      this.phase = "wrong";
      audio.wrongThud();
      this.hooks.onShake?.(dir);
      this.emitHud("−3.0s");
      this.drawStones();
    }
  }

  afterWalk() {
    if (this.stepI >= this.spec.pathLength) {
      this.finish(true);
      return;
    }
    this.phase = "idle";
    this.drawStones();
    if (this.settings.readAloud && this.spec.grade <= 2) {
      audio.speak(this.spec.steps[this.stepI].problem.speak);
    }
    this.emitHud();
  }

  finish(completed: boolean) {
    if (this.phase === "done" || this.phase === "timeout") return;
    this.phase = completed ? "done" : "timeout";
    this.revealT = 0;
    if (completed) {
      for (let r = 0; r < this.spec.size; r++) for (let c = 0; c < this.spec.size; c++) this.visited[r][c] = true;
      audio.celebrate();
      this.hopT = 1;
    }
    this.overlay.innerHTML = "";
    const secondsLeft = this.remainingMs / 1000;
    const br = finalizeScore({
      stepPoints: this.score,
      finished: completed,
      misses: this.misses,
      firstTry: this.firstTry,
      stepsDone: this.stepI,
      pathLength: this.spec.pathLength,
      secondsLeft,
      timeMs: Math.round(performance.now() - this.startAt),
      streakBest: this.streakBest,
    });
    this.score = br.total;
    this.emitHud();
    if (completed) this.hooks.onComplete(br, this.events);
    else this.hooks.onTimeout(br, this.events);
  }

  pause(on: boolean) {
    if (this.phase === "done" || this.phase === "timeout") return;
    this.phase = on ? "paused" : "idle";
  }

  emitHud(combo = "") {
    this.hooks.onHud({ score: this.score, streak: this.streak, combo, remainingMs: this.remainingMs });
  }

  loop(ts: number) {
    if (this.destroyed) return;
    const dt = this.lastTs ? Math.min(50, ts - this.lastTs) : 16;
    this.lastTs = ts;
    if (this.phase === "idle" || this.phase === "wrong") {
      this.remainingMs -= dt;
      if (this.remainingMs <= 0) {
        this.remainingMs = 0;
        this.finish(false);
      }
    }
    if (this.phase === "walk") {
      this.walkT += dt / (this.reduced ? 1 : WALK_MS);
      if (this.walkT >= 1) {
        this.walkT = 1;
        this.afterWalk();
      }
    }
    if (this.phase === "wrong") {
      this.wrongT += dt;
      if (this.wrongT > 380) {
        this.phase = "idle";
        this.wrongDir = null;
        this.drawStones();
      }
    }
    if (this.phase === "done") this.revealT = Math.min(1, this.revealT + dt / 1000);
    if (this.hopT > 0) this.hopT = Math.max(0, this.hopT - dt / 420);
    this.draw(ts);
    this.raf = requestAnimationFrame(this.loop);
    this.hudAcc += dt;
    if (this.hudAcc > 100 && (this.phase === "idle" || this.phase === "walk" || this.phase === "wrong")) {
      this.hudAcc = 0;
      this.hooks.onHud({
        score: this.score,
        streak: this.streak,
        combo: "",
        remainingMs: this.remainingMs,
      });
    }
  }

  draw(ts: number) {
    const ctx = this.ctx;
    const w = this.canvas.clientWidth;
    ctx.clearRect(0, 0, w, w);
    const size = this.spec.size;
    const cell = this.cell;
    const pad = this.pad;

    ctx.fillStyle = "#070605";
    roundRect(ctx, 0, 0, w, w, 16);
    ctx.fill();

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const x = pad + c * cell;
        const y = pad + r * cell;
        const vis = this.visited[r][c] || (this.phase === "done" && this.revealT > 0.2);
        ctx.save();
        ctx.beginPath();
        ctx.rect(x + 1, y + 1, cell - 2, cell - 2);
        ctx.clip();
        if (!vis) {
          ctx.fillStyle = "#070605";
          ctx.fillRect(x, y, cell, cell);
        } else if (this.art) {
          const sx = (c / size) * this.art.width;
          const sy = (r / size) * this.art.height;
          const sw = this.art.width / size;
          const sh = this.art.height / size;
          let a = 1;
          if (this.phase === "done" && !this._wasVisited(r, c)) a = this.revealT;
          ctx.globalAlpha = a;
          ctx.drawImage(this.art, sx, sy, sw, sh, x, y, cell, cell);
          ctx.globalAlpha = 1;
          ctx.strokeStyle = this.look.glow;
          ctx.lineWidth = Math.max(2, cell * 0.06);
          ctx.strokeRect(x + 2, y + 2, cell - 4, cell - 4);
          drawStamp(ctx, x + cell * 0.5, y + cell * 0.78, cell * 0.12, this.look.stamp);
        } else {
          ctx.fillStyle = "#2a2118";
          ctx.fillRect(x, y, cell, cell);
        }
        ctx.restore();
        ctx.strokeStyle = vis ? "rgba(212, 160, 70, 0.18)" : "rgba(20, 16, 12, 0.9)";
        ctx.strokeRect(x + 0.5, y + 0.5, cell - 1, cell - 1);
      }
    }

    // worn trail
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "rgba(120, 78, 32, 0.45)";
    ctx.lineWidth = Math.max(6, cell * 0.18);
    ctx.beginPath();
    for (let i = 0; i <= this.stepI && i < this.spec.path.length; i++) {
      const p = this.cellCenter(this.spec.path[i].r, this.spec.path[i].c);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    if (this.phase === "walk") {
      const t = ease(this.walkT);
      const x = this.walkFrom.x + (this.walkTo.x - this.walkFrom.x) * t;
      const y = this.walkFrom.y + (this.walkTo.y - this.walkFrom.y) * t;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.strokeStyle = "rgba(212, 160, 70, 0.35)";
    ctx.lineWidth = Math.max(3, cell * 0.08);
    ctx.stroke();

    // start + finish marks
    const st = this.cellCenter(this.spec.path[0].r, this.spec.path[0].c);
    drawPaw(ctx, st.x, st.y, cell * 0.12);
    const fin = this.spec.path[this.spec.path.length - 1];
    const fc = this.cellCenter(fin.r, fin.c);
    drawLantern(ctx, fc.x, fc.y - cell * 0.08, cell * 0.22);

    // Pip
    let px: number, py: number;
    if (this.phase === "walk") {
      const t = ease(this.walkT);
      px = this.walkFrom.x + (this.walkTo.x - this.walkFrom.x) * t;
      py = this.walkFrom.y + (this.walkTo.y - this.walkFrom.y) * t;
    } else {
      const p = this.cellCenter(this.pip.r, this.pip.c);
      px = p.x;
      py = p.y;
    }
    const bob = this.phase === "walk" ? Math.sin(this.walkT * Math.PI) * cell * 0.08 : Math.sin(ts / 280) * cell * 0.03;
    const hop = this.hopT > 0 ? Math.sin(this.hopT * Math.PI) * cell * 0.28 : 0;
    this.drawPip(px, py - bob - hop, ts, this.phase === "walk");
  }

  _wasVisited(r: number, c: number) {
    return this.spec.path.some((p, i) => i <= this.spec.pathLength && p.r === r && p.c === c);
  }

  drawPip(x: number, y: number, ts: number, walking: boolean) {
    const ctx = this.ctx;
    const s = this.cell * 0.72;
    ctx.save();
    ctx.translate(x, y);
    if (this.facing === "W") ctx.scale(-1, 1);
    const tail = Math.sin(ts / 260) * 0.18;
    if (this.pipImg) {
      ctx.rotate(walking ? Math.sin(this.walkT * Math.PI * 4) * 0.08 : tail * 0.08);
      ctx.drawImage(this.pipImg, -s / 2, -s / 2, s, s);
    } else {
      drawFallbackPip(ctx, s, ts, walking);
    }
    ctx.restore();
  }

  drawStones() {
    const step = this.spec.steps[this.stepI];
    if (!step || this.phase === "done" || this.phase === "timeout") {
      this.overlay.innerHTML = "";
      return;
    }
    const size = this.spec.size;
    // Four compass stones always — never hide a heading, even if the cell is on an edge.
    const stones = DIRS.map((dir) => {
      const v = step.stones[dir];
      const shake = this.wrongDir === dir ? " is-shake" : "";
      const shape = dir === "N" ? "▲" : dir === "S" ? "▼" : dir === "E" ? "▶" : "◀";
      return `<button type="button" class="stone stone-${dir}${shake}" data-dir="${dir}" aria-label="${dir} ${v.speak}">
        <span class="stone-dir" aria-hidden="true">${shape}<abbr>${dir}</abbr></span>
        <span class="stone-ans">${escapeHtml(v.compact)}</span>
      </button>`;
    }).join("");

    const hint =
      this.spec.mode === "practice" && this.triesOnTile >= 2
        ? `<p class="hint">${escapeHtml(step.problem.hint)}</p>`
        : this.spec.mode === "practice" && this.triesOnTile >= 1
          ? `<p class="hint">Still on this stone. Try another heading.</p>`
          : "";

    const pr = (step.cell.r + 0.5) / size;
    const pc = (step.cell.c + 0.5) / size;

    this.overlay.innerHTML = `
      <div class="sign" role="status">${renderTokensHtml(step.problem.tokens, true)}</div>
      <div class="stones" style="--pr:${pr};--pc:${pc}">${stones}</div>
      ${hint}
    `;
    this.overlay.querySelectorAll<HTMLButtonElement>(".stone").forEach((btn) => {
      btn.addEventListener("click", () => this.tryDir(btn.dataset.dir as Dir));
    });
  }

  destroy() {
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("resize", this.layout);
    this.ro?.disconnect();
  }
}

function pointsMultLabel(streak: number): string {
  if (streak >= 9) return "2";
  if (streak >= 6) return "1.5";
  if (streak >= 3) return "1.2";
  return "1";
}

function ease(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawPaw(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.fillStyle = "rgba(212, 180, 90, 0.35)";
  ctx.beginPath();
  ctx.ellipse(x, y + r * 0.2, r * 0.9, r * 0.7, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawStamp(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, kind: StampId) {
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = "rgba(30, 22, 14, 0.35)";
  ctx.strokeStyle = "rgba(30, 22, 14, 0.45)";
  ctx.lineWidth = 1.4;
  if (kind === "boot") {
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 0.55, r * 0.9, 0, 0, Math.PI * 2);
    ctx.fill();
  } else if (kind === "cleat") {
    ctx.beginPath();
    ctx.rect(-r * 0.5, -r * 0.7, r, r * 1.3);
    ctx.fill();
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.arc(i * r * 0.28, r * 0.55, r * 0.12, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (kind === "gear") {
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.25, 0, Math.PI * 2);
    ctx.fill();
  } else if (kind === "quilltick") {
    ctx.beginPath();
    ctx.moveTo(-r, 0);
    ctx.lineTo(r, 0);
    ctx.moveTo(r * 0.2, -r * 0.4);
    ctx.lineTo(r, 0);
    ctx.lineTo(r * 0.2, r * 0.4);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.ellipse(0, r * 0.15, r * 0.7, r * 0.55, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(-r * 0.45, -r * 0.35, r * 0.28, r * 0.28, 0, 0, Math.PI * 2);
    ctx.ellipse(r * 0.45, -r * 0.35, r * 0.28, r * 0.28, 0, 0, Math.PI * 2);
    ctx.ellipse(-r * 0.15, -r * 0.6, r * 0.22, r * 0.22, 0, 0, Math.PI * 2);
    ctx.ellipse(r * 0.2, -r * 0.6, r * 0.22, r * 0.22, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawLantern(ctx: CanvasRenderingContext2D, x: number, y: number, s: number) {
  ctx.fillStyle = "#2F5D3A";
  ctx.fillRect(x - s * 0.12, y - s, s * 0.24, s * 0.5);
  ctx.fillStyle = "#E8A317";
  ctx.beginPath();
  ctx.ellipse(x, y - s * 0.2, s * 0.35, s * 0.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff6d8";
  ctx.beginPath();
  ctx.ellipse(x, y - s * 0.2, s * 0.16, s * 0.2, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawFallbackPip(ctx: CanvasRenderingContext2D, s: number, ts: number, walking: boolean) {
  ctx.fillStyle = "#E86A1F";
  ctx.beginPath();
  ctx.ellipse(0, 0, s * 0.32, s * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(s * 0.18, -s * 0.18, s * 0.2, s * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
